import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import Stepper from "../stepper/Stepper";
import { StepFlowHeader } from "../step-flow/StepFlowShell";
import { STEP_FLOW_CONTENT_MAX_WIDTH } from "../step-flow/stepFlowActions";
import Button from "../button/Button";
import StatusIndicator from "../status-indicator/StatusIndicator";
import type { StatusType } from "../status-indicator/StatusIndicator";
import OfferConfirmModal, {
  type OfferConfirmPdfPreview,
} from "./OfferConfirmModal";
import EmailInviteModal from "./EmailInviteModal";
import { useOfferWheelLayout } from "./useOfferWheelLayout";
import useElementRect from "../../hooks/useElementRect";
import {
  formatPdfExportError,
  requestOfferSubcontractPdf,
} from "../../app/services/pdfExports";
import {
  assignServerDmId,
  discardWizard,
  dmThreadKey,
  hydrateOfferFormFromDetail,
  patchData,
  patchSummary,
  selectAllOfferDrafts,
  selectOfferWizardEntry,
  setPrefillSource,
  setRoleId,
  setStep,
  setStepOrder,
  setSyncStatus,
} from "../../app/offerWizard/offerWizardSlice";
import { computeCompensationSubtotalExclusive } from "../../utils/offerCompensationLines";
import {
  type OfferDraftSyncStatus,
  type OfferFormState,
  type OfferPrefillSource,
  type OfferRecipientState,
  type OfferStep,
  type OfferWizardEntry,
  type StepOrder,
} from "./types";
import { useScrollDirectionLayoutSync } from "../../hooks/useScrollDirectionLayoutSync";
import RecipientStep, {
  type RecipientStepUserSelected,
} from "./steps/RecipientStep";
import RoleStep from "./steps/RoleStep";
import DetailFormStep from "./steps/DetailFormStep";
import CompleteStep from "./steps/CompleteStep";
import {
  useCreateDmThreadMutation,
  useGetDmThreadDetailQuery,
  useUpdateDmThreadMutation,
  type CreateDmThreadParams,
} from "../../app/services/dmThreads";
import { useGetRolesQuery, useGetRoleUsersQuery } from "../../app/services/roles";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { isSpecialistPosition } from "../../utils/memberPositionUtils";
import { useGetFollowsQuery, type FollowUser } from "../../app/services/follows";
import {
  roleStatusIcon,
  roleStatusLabel,
} from "./roleAcceptPosition";
import { pickRoleDisplayName } from "./roleDisplay";
import {
  buildOfferThreadPatchBody,
  useOfferDraftAutosave,
} from "../../hooks/useOfferDraftAutosave";
import type { OfferEscrowBalanceCheck } from "../../utils/balance";
import { useOfferWizardSelectionConsistency } from "../../hooks/useOfferWizardSelectionConsistency";
import { useObservedHeight } from "../../hooks/useObservedHeight";
import { sumCompensationLines } from "../../utils/offerCompensationLines";
import { getUserDisplayName } from "../../utils/user/displayName";
import {
  getCapacityLabel,
  isCapacityFresh,
} from "../../utils/user/capacity";
import { getOffDates } from "../../utils/schedule/upcomingSchedule";
import Avatar from "../avatar/Avatar";
import SpeechBubble from "../speech-bubble/SpeechBubble";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../date-display/DateDisplay";
import type { OfferSummaryCardData } from "./OfferSummaryCard";

/** OfferStep ごとのステッパー表示ラベル。 */
const STEP_LABEL_BY_STEP: Record<OfferStep, string> = {
  recipient: "送信先",
  role: "ロール",
  form: "詳細",
  complete: "完了",
};

/**
 * stepOrder に応じたステップの並び（左から順、1-origin で stepNumber に対応）。
 * `orderedSteps(stepOrder)[i]` が i+1 番目のステップを返す。
 */
const orderedSteps = (stepOrder: StepOrder): readonly OfferStep[] =>
  stepOrder === "role_first"
    ? ["role", "recipient", "form", "complete"]
    : ["recipient", "role", "form", "complete"];

const stepOneOf = (stepOrder: StepOrder): OfferStep => orderedSteps(stepOrder)[0];
const stepTwoOf = (stepOrder: StepOrder): OfferStep => orderedSteps(stepOrder)[1];

const buildStepLabels = (stepOrder: StepOrder): string[] =>
  orderedSteps(stepOrder).map((s) => STEP_LABEL_BY_STEP[s]);

/** OfferStep → stepNumber (1-origin) を stepOrder から計算。 */
const offerStepToStepNumber = (
  stepOrder: StepOrder,
  step: OfferStep,
): number => orderedSteps(stepOrder).indexOf(step) + 1;

interface OfferWizardOverlayProps {
  threadKey: string;
  projectId: string;
  /**
   * `selectedDraftKey` が `assignServerDmId` でリネームされた直後に親側で再選択する
   * ためのコールバック。引数には新しい threadKey（`dm:<dm_id>`）が渡される。
   */
  // eslint-disable-next-line no-unused-vars
  onThreadKeyAssigned?: (nextThreadKey: string) => void;
  /**
   * オファー送信（contractor_pending 遷移）成功直後に呼ばれる。
   * 親側で該当スレッドを選択状態にしてメッセンジャー表示へ切り替えるために使う
   * （deep-link は初回マウント時のみ適用されるため送信後は明示通知が必要）。
   */
  // eslint-disable-next-line no-unused-vars
  onSubmitted?: (dmId: string) => void;
  /**
   * 編集モード。`true` のとき、既に送信済み（contractor_pending）のオファーの
   * 内容を編集するために開く。送信ボタンは status 遷移せず、差分を保存して戻る。
   */
  editMode?: boolean;
}

// ===== 視覚要素 =====

const OverlayRoot = styled.div`
  position: absolute;
  inset: 0;
  /* BottomAppBar (z-index: 4) より下層に置き、スクロールで下端まで
     達したフォーム内容が BottomAppBar の手前に出ないようにする。 */
  z-index: 3;
  display: flex;
  flex-direction: column;
  background: transparent;
`;

/**
 * ステップナビゲーションを内包するヘッダー（中身は共通の `StepFlowHeader`）。
 *
 * arc モード時の `RadioCardGroup` の Stage は `position: fixed; z-index: 2` で
 * viewport 中央を基準に描画されるため、何もしないと初期表示時にカードが Stepper の
 * 領域までせり上がり、ステップナビゲーションを覆ってしまう。
 *
 * Stage(z=2) および FilterChipBar / WizardActionPanel / SyncStatusFloater (z=3) より
 * 上層に置くため、`position: relative; z-index: 4` で stacking context を確立する。
 */
const OverlayHeader = styled.div`
  position: relative;
  z-index: 4;
  background: transparent;
`;

/**
 * `data-offer-overlay-scroll`（JSX 側で付与）は内部の RadioCardGroup
 *（arc variant）が scroll listener を貼る "scroller" 要素を自動検出するための
 * マーカー。値は使わず、`closest('[data-offer-overlay-scroll]')` で発見できれば良い。
 */
const OverlayBody = styled.div<{ $bottomPadding: number }>`
  flex: 1;
  overflow-y: auto;
  /* 左右 padding を撤去し、ActionPanel と幅を揃える。下端のみ
     BottomAppBar / ActionPanel との重なり回避用に余白を確保。 */
  padding-bottom: ${({ $bottomPadding }) => $bottomPadding}px;
`;

/**
 * 戻る/最終確認へ を置く下部固定パネル。CommentField (`DmActionPanel`) 内側の
 * グラスモーフィズム枠と同じ見た目（半透明 + backdrop-filter + 全周 border-radius）
 * に揃える。BottomAppBar からは少し（`PANEL_BOTTOM_GAP`）離して浮かせる。
 */
const WizardActionPanel = styled.div<{
  $bottom: number;
  $left: number;
  $width: number;
}>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  /* BottomAppBar (z-index: 4) より下層に。重なったときは BottomAppBar が手前。 */
  z-index: 3;
  padding: 8px 12px;

  /* グラスモーフィズム（CommentField の Root と統一） */
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);

  transition: bottom 0.15s linear;
`;

/** ActionPanel 内のボタン列。他のステップフローと同じ幅上限で中央寄せ */
const WizardActionPanelInner = styled.div`
  width: 100%;
  max-width: ${STEP_FLOW_CONTENT_MAX_WIDTH}px;
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const ErrorText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
  margin-top: 12px;
`;

/**
 * ドラフト保存ステータスを画面全体（viewport）の右下にフロート表示する。
 *  - 右モーダル幅に縛られず viewport 右端 24px に固定（ActionPanel 内に
 *    収めようとすると視覚的に被るため、外側に逃がしている）
 *  - BottomAppBar の表示状態に応じた `actionPanelBottom` 追従に加え、
 *    ActionPanel の実高さ（`footerHeight`）も上乗せして重なりを回避する
 *  - クリック誤検知を避けるため `pointer-events: none`
 */
const SyncStatusFloater = styled.div<{ $bottom: number }>`
  position: fixed;
  right: 24px;
  bottom: ${({ $bottom }) => $bottom}px;
  z-index: 3;
  pointer-events: none;
  transition: bottom 0.15s linear;
`;

/**
 * ActionPanel の真上に置く "副パネル"。arc モードでは:
 *  - 送信先ステップ → 選択中ユーザーの詳細プレビュー
 *  - ロールステップ → 選択中ロールの role_name_jp / description プレビュー
 * を中に入れる（どちらも未選択なら描画しない）。
 *
 * 位置は ActionPanel と同じく fixed で、`$bottom` に
 * `actionPanelBottom + footerHeight + ACTION_PANEL_BOTTOM_BUFFER` を渡すことで
 * ActionPanel の真上に重ならず張り付く。
 *
 * ActionPanel のような外枠（半透明 + backdrop-filter + border）は重ね描きで
 * くどく見えるため敢えて持たず、純粋な配置ラッパーとして使う。中身
 * （UserPreview / RolePreview）が背景・余白を自分で持っている前提。
 */
const SecondaryPanel = styled.div<{
  $bottom: number;
  $left: number;
  $width: number;
}>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  z-index: 3;
  transition: bottom 0.15s linear;
`;

/**
 * 選択中ロールのテキストプレビュー（副パネル内ブロック）。
 *
 * SecondaryPanel 自体は外枠を持たないので、ここで最小限の背景・余白を持つ。
 * 半透明 + backdrop-filter で、後ろを通る carousel カードがぼんやり透けて見える
 * 状態にしている（=ユーザー要望「上下にはみ出したカードが透けて表示される」）。
 */
const RolePreviewBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 6px;
`;

/**
 * 選択中ロールのプレビュー左側に出すポジションアイコン枠。
 *
 * アイコンの有無で横レイアウトが揺れないよう、常に 32×32 の固定サイズで領域を予約する。
 * アイコンが無い場合は空の枠（透明）だけが残り、テキスト側の左 padding が一定に保たれる。
 * 半透明背景上に置かれるため `mix-blend-mode` は使わず、`var(--soremo-light-gray)` 単色で
 * 控えめに描画する（サムネ画像のないコンテキストなのでコントラスト反転は不要）。
 *
 * `$compact=true`（= `hub` アイコン）のときはグリフサイズを 24px に縮め、他のアイコンと
 * 見た目バランスを揃える。枠サイズ自体は 32px のまま固定でレイアウトに揺れを出さない。
 */
const RolePreviewIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $compact?: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: ${({ $compact }) => ($compact ? "24px" : "32px")};
  color: var(--soremo-light-gray);
  font-variation-settings: ${({ $compact }) =>
    $compact
      ? `"FILL" 0, "wght" 500, "GRAD" 0, "opsz" 24`
      : `"FILL" 0, "wght" 500, "GRAD" 0, "opsz" 32`};
`;

const RolePreviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RolePreviewTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const RolePreviewTitle = styled.div.attrs({
  className: "heading-13 u-line-height-150",
})`
  color: var(--soremo-text);
`;

/**
 * 見出しの隣にコンパクトに添える英語名表記。再クリック切替により
 * `role_name_en` / `credit_title_en` のどちらかを表示する（両方空のときは非表示）。
 * DIN 2014 ExtraBold + uppercase でカード右辺の縦書きと統一感を持たせる。
 */
const RolePreviewTitleEn = styled.div`
  font-family: "din-2014", sans-serif;
  font-weight: 800;
  font-style: normal;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--soremo-light-gray);
`;

const RolePreviewDescription = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-text);
  margin-top: 4px;
  /* 長文時に副パネルが伸びすぎないよう 3 行で省略する。 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/**
 * 選択中ユーザーのテキストプレビュー（副パネル内ブロック）。
 *
 * 構成・背景・余白は `RolePreviewBlock` と揃え、ロール選択時の UI と統一感を保つ。
 * レイアウトは「アバター + 忙しさ吹き出し（左縦列）| 本文（右縦列）」の 2 段。
 * 本文はラベル文字を出さずに、表示名・タイトル・クライテリア本文・スケジュール本文・
 * NG 日リストを並べる（情報量はそのままで視覚ノイズを抑える）。
 */
const UserPreviewBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 6px;
`;

/**
 * 左列: アバターの真下に忙しさ SpeechBubble を縦並びで置く。
 * `align-items: center` で吹き出しがアバターの中心線にぶら下がる見た目になる。
 */
const UserPreviewAvatarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: none;
`;

const UserPreviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

/**
 * 表示名 + 英語名のヘッダー行。`RolePreviewTitleRow` と同じパターン。
 */
const UserPreviewTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const UserPreviewTitle = styled.div.attrs({
  className: "heading-13 u-line-height-150",
})`
  color: var(--soremo-text);
`;

const UserPreviewTitleEn = styled.div`
  font-family: "din-2014", sans-serif;
  font-weight: 800;
  font-style: normal;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--soremo-light-gray);
`;

/** タイトル（職種）の小行。 */
const UserPreviewMeta = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
`;

/**
 * クライテリア / スケジュール概要などのテキスト系セクション。
 * ラベルは表示せず、本文 1 行 + NG 日リスト等を縦に積むだけの小ブロック。
 */
const UserPreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
`;

/** プレビュー本文の 1 行（クライテリア・スケジュール概要などに使う）。 */
const UserPreviewText = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-text);
`;

/**
 * NG 日リスト。最大 21 日分入る想定なので `flex-wrap` で折返して縦伸びを許容する。
 */
const UserPreviewNgDateList = styled.div.attrs({
  className: "bodytext-13",
})`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  color: var(--soremo-text);
`;

interface UserPreviewContentProps {
  user: FollowUser;
}

/**
 * 選択中ユーザーのプレビュー UI。
 *
 * 各セクションは個別に「値があるときだけ表示」する:
 *  - クライテリア: `offer_criteria` が空のときは非表示。
 *  - 忙しさ: `isCapacityFresh(capacity_index_updated_at)` が `false` のとき非表示
 *    （3 日以上更新されていない値は誤判断防止のため出さない）。
 *  - スケジュール概要: `schedule_overview` が空のときは非表示。
 *  - 直近の NG 日: `upcoming_schedule` の `off` を全件並べる。0 件のとき非表示。
 *
 * すべて空のとき (CREATOR ロール以外、または設定未入力) は、表示名 / タイトルのみの
 * 簡素なプレビューになる。
 */
const UserPreviewContent: React.FC<UserPreviewContentProps> = ({ user }) => {
  const displayNameJp = getUserDisplayName(user, "jp");
  const displayNameEn = getUserDisplayName(user, "en");
  const showEnName = !!displayNameEn && displayNameEn !== displayNameJp;
  const titleText = user.title_jp || user.title_en || "";
  const affiliationText = user.affiliation_jp || user.affiliation_en || "";

  const criteriaText = user.offer_criteria?.trim() || "";
  const scheduleText = user.schedule_overview?.trim() || "";
  const offDates = getOffDates(user.upcoming_schedule);

  // capacity_index は 3 日でリセットされる仕様なので、鮮度切れの値は出さない。
  const capacityLabel = isCapacityFresh(user.capacity_index_updated_at)
    ? getCapacityLabel(user.capacity_index)
    : null;

  return (
    <UserPreviewBlock>
      <UserPreviewAvatarColumn>
        <Avatar
          size={40}
          src={user.avatar_url ?? undefined}
          name={displayNameJp || displayNameEn}
        />
        {capacityLabel && (
          <SpeechBubble
            direction="top"
            aria-label={`忙しさ: ${capacityLabel}`}
          >
            {capacityLabel}
          </SpeechBubble>
        )}
      </UserPreviewAvatarColumn>

      <UserPreviewBody>
        <UserPreviewTitleRow>
          {displayNameJp && (
            <UserPreviewTitle>{displayNameJp}</UserPreviewTitle>
          )}
          {showEnName && (
            <UserPreviewTitleEn>{displayNameEn}</UserPreviewTitleEn>
          )}
        </UserPreviewTitleRow>

        {(titleText || affiliationText) && (
          <UserPreviewMeta>
            {[titleText, affiliationText].filter(Boolean).join(" / ")}
          </UserPreviewMeta>
        )}

        {criteriaText && (
          <UserPreviewSection>
            <UserPreviewText>{criteriaText}</UserPreviewText>
          </UserPreviewSection>
        )}

        {(scheduleText || offDates.length > 0) && (
          <UserPreviewSection>
            {scheduleText && <UserPreviewText>{scheduleText}</UserPreviewText>}
            {offDates.length > 0 && (
              <UserPreviewNgDateList aria-label="直近のNG日">
                {offDates.map((d, i) => (
                  <span key={d}>
                    <DateDisplay date={d} type={DATE_DISPLAY_TYPE.DATE_ONLY} />
                    {i < offDates.length - 1 ? "、" : ""}
                  </span>
                ))}
              </UserPreviewNgDateList>
            )}
          </UserPreviewSection>
        )}
      </UserPreviewBody>
    </UserPreviewBlock>
  );
};

/**
 * `OfferDraftSyncStatus` をグローバル `StatusIndicator` の `StatusType` に変換する。
 * `synced` は意味的に「保存済み」なので `saved` にマッピングする。
 */
const toGlobalStatusType = (status: OfferDraftSyncStatus): StatusType => {
  switch (status) {
    case "creating":
      return "creating";
    case "saving":
      return "saving";
    case "synced":
      return "saved";
    case "error":
      return "error";
    case "idle":
    default:
      return "idle";
  }
};

// ===== バリデーション =====

const isRecipientReady = (recipient: OfferRecipientState): boolean => {
  if (recipient.mode === "follows") return !!recipient.userId;
  if (recipient.mode === "email") return !!recipient.email;
  return false;
};

/**
 * 送信先ステップで「次へ」を押せる状態か。
 *
 * 「メールアドレスで招待」はカード選択の時点ではまだアドレスが空なので、
 * `isRecipientReady`（= 送信に足る受信者が確定しているか）とは判定を分ける。
 * カードを選んだ状態で「次へ」を押すと入力モーダルが開き、そこで確定してから
 * 次ステップへ進む（ユーザーカードを選んで「次へ」を押す導線と揃えるため）。
 */
const canLeaveRecipientStep = (recipient: OfferRecipientState): boolean =>
  recipient.mode === "email" || isRecipientReady(recipient);

/** 現在のステップ①に該当する選択がまだ無いかどうか。 */
const isStepOneEmpty = (entry: OfferWizardEntry): boolean => {
  const stepOne = stepOneOf(entry.stepOrder);
  if (stepOne === "recipient") return !isRecipientReady(entry.data.recipient);
  return !entry.data.roleId;
};

/**
 * 最終確認モーダルを開ける状態かを判定する（FE 側必須項目チェック）。
 * 仕様書 §5「new → contractor_pending」の必須フィールドに準拠:
 *  - 受信者
 *  - ロール
 *  - スコープ / 納期 / 対価のいずれか少なくとも 1 件は埋まっている
 */
const isReadyForConfirm = (entry: OfferWizardEntry): boolean => {
  if (!isRecipientReady(entry.data.recipient)) return false;
  if (!entry.data.roleId) return false;
  // 「サーバ側に dm レコードが存在する」ことも前提
  if (!entry.serverDmId) return false;
  return true;
};

const BOTTOM_BAR_HEIGHT = 80;
const ACTION_PANEL_BOTTOM_BUFFER = 24;
const BODY_MIN_PADDING_BOTTOM = 120;
// パネルを BottomAppBar から少しだけ浮かせる距離。
const PANEL_BOTTOM_GAP = 4;
// ActionPanel と StatusIndicator の間に確保する縦方向の隙間。
const SYNC_STATUS_GAP_ABOVE_ACTION_PANEL = 8;

const DEFAULT_ESCROW_BALANCE_CHECK: OfferEscrowBalanceCheck = {
  applicable: false,
  sufficient: true,
  contractAmountExclusive: null,
  availableExclusive: 0,
  reservedByOtherDraftsExclusive: 0,
  shortfallExclusive: 0,
};

/**
 * 送信先 / ロール選択ステップを「円形/扇形 → ホイール回転」スクロール演出（RadioCardGroup）に
 * 切り替えるためのコンパイル時フラグ。
 *
 *  - false: 既存挙動（送信先 = Swiper Coverflow、ロール = 縦リスト）
 *  - true : arc バリアントへ切替
 *
 * 本番投入時は環境変数 / Redux のユーザー設定に置き換える前提（今回スコープ外）。
 */
const USE_ARC_PICKER = true;

// ===== 本体 =====

const OfferWizardOverlay: React.FC<OfferWizardOverlayProps> = ({
  threadKey,
  projectId,
  onThreadKeyAssigned,
  onSubmitted,
  editMode = false,
}) => {
  const dispatch = useDispatch();
  const entry = useSelector(selectOfferWizardEntry(threadKey));
  const [searchParams, setSearchParams] = useSearchParams();

  const [createDmThread, { isLoading: isCreatingDraft }] =
    useCreateDmThreadMutation();
  const [updateDmThread, { isLoading: isFinalizing }] =
    useUpdateDmThreadMutation();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /**
   * 「メールアドレスで招待」の入力モーダル。カード選択後の「次へ」と、
   * 選択済みカードの再クリック（アドレス編集）の両方から開く。
   */
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  /** 送信先 / ロールホイールの Shift ホールド中か（副パネルを隠す）。 */
  const [wheelStacked, setWheelStacked] = useState(false);
  const [escrowBalanceCheck, setEscrowBalanceCheck] =
    useState<OfferEscrowBalanceCheck>(DEFAULT_ESCROW_BALANCE_CHECK);

  const handleEscrowBalanceChange = useCallback(
    (check: OfferEscrowBalanceCheck) => {
      setEscrowBalanceCheck(check);
    },
    [],
  );

  // フォームステップ以外では残高チェックをリセット
  useEffect(() => {
    if (entry?.currentStep !== "form") {
      setEscrowBalanceCheck(DEFAULT_ESCROW_BALANCE_CHECK);
    }
  }, [entry?.currentStep]);

  // ステップが変わったら、ホイール由来の一時状態（招待モーダル / スタック中）を畳む。
  // ホイールごと unmount されるので keyup を取りこぼしたスタック状態が残らないようにする。
  useEffect(() => {
    setEmailModalOpen(false);
    setWheelStacked(false);
  }, [entry?.currentStep]);

  // 他の未送信ドラフト（このウィザード以外で編集中・送信前）が予約している対価合計。
  // 残高はユーザー単位（全プロジェクト共通）なので全ドラフトを横断して集計する。
  // 送信済みオファーは BE の escrow_lock で既に拘束済みのため、ここには含まれない
  //（送信成功で discardWizard され byThreadKey から除かれる）。
  const allDrafts = useSelector(selectAllOfferDrafts);
  const reservedByOtherDraftsExclusive = useMemo(
    () =>
      allDrafts
        .filter(
          ({ threadKey: key, entry: e }) =>
            key !== threadKey && e.currentStep !== "complete",
        )
        .reduce(
          (sum, { entry: e }) =>
            sum + computeCompensationSubtotalExclusive(e.data.form.compensationLines),
          0,
        ),
    [allDrafts, threadKey],
  );

  /**
   * 最終確認モーダルに渡す PDF プレビュー状態。
   * `confirmOpen` 切り替えと連動するが、PDF 取得は非同期なので独立の state を持つ。
   */
  const [pdfPreview, setPdfPreview] = useState<OfferConfirmPdfPreview | null>(
    null,
  );

  // BottomAppBar との重なり回避
  const scrollState = useSelector(
    (state: { layout: string }) => state.layout,
  );

  // SPECIALIST は BottomAppBar 非表示（DM のみアクセス可）のため、
  // ActionPanel をスクロールに連動させず常に viewport 下端付近で固定する。
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId, {
    skip: !projectId || !currentUserData,
  });
  const isSpecialist = isSpecialistPosition(
    membershipData?.data?.position ?? "",
  );

  // SPECIALIST は BAB がないため BOTTOM_BAR_HEIGHT を加算せず、
  // スクロールにも連動させない（PANEL_BOTTOM_GAP のみで固定）。
  const actionPanelBottom = isSpecialist
    ? PANEL_BOTTOM_GAP
    : (scrollState !== "down" ? BOTTOM_BAR_HEIGHT : 0) + PANEL_BOTTOM_GAP;

  // 内側スクロール（OverlayBody）→ state.layout への連動（DM の MessengerContent と
  // 同方式のフェイルセーフ）。ウィザードは実スクロールが OverlayBody
  //（`overflow-y: auto`）で完結するため、特に SP では window レベル検出（GlobalWrapper）
  // が取りこぼしやすい。wheel / touchmove のユーザー操作を直接拾って方向を判定し
  // dispatch することで、BottomAppBar 表示/非表示と確実に連動させる。
  const bodyScrollSync = useScrollDirectionLayoutSync();

  const overlayRef = useRef<HTMLDivElement>(null);
  const overlayRect = useElementRect(overlayRef);
  // ステッパーを含むヘッダーの実高。arc ホイールのカード領域を
  // 「ヘッダーの下 〜 下部パネルの上」に収めるための上端基準に使う。
  const { ref: headerRef, height: headerHeight } =
    useObservedHeight<HTMLDivElement>();
  const { ref: footerRef, height: footerHeight } =
    useObservedHeight<HTMLDivElement>();
  // arc モード時に ActionPanel の真上に出す副パネル（選択中ユーザー / ロールの
  // プレビュー）の高さも測って、OverlayBody の下端余白に加える。
  const { ref: secondaryPanelRef, height: secondaryPanelHeight } =
    useObservedHeight<HTMLDivElement>();

  // フッターは画面下に fixed 配置するため、OverlayBody の下端には
  // 「フッター実高さ + 副パネル実高さ + BottomAppBar 分 + バッファ」の
  // 余白を確保する。副パネルが無いステップでは secondaryPanelHeight = 0。
  const bodyPaddingBottom = Math.max(
    BODY_MIN_PADDING_BOTTOM,
    footerHeight +
      secondaryPanelHeight +
      actionPanelBottom +
      ACTION_PANEL_BOTTOM_BUFFER,
  );

  // arc ホイール（送信先 / ロール）のカード寸法と縦中心。ホーム・プロジェクトの
  // ホイールと同じく、固定サイズではなく「実際に空いている領域」へ白銀比でフィット
  // させ、下部パネルに潜らない位置へ寄せる。
  const wheelLayout = useOfferWheelLayout({
    overlayTop: overlayRect.top,
    overlayWidth: overlayRect.width,
    headerHeight,
    reservedBottom: bodyPaddingBottom,
    enabled: USE_ARC_PICKER,
  });

  /**
   * ウィザードを開いた / ドラフトを切り替えた / ステップを進めた直後に、起動元の
   * ボタンへフォーカスが残るのを解除する。
   *
   * 左カラムの「新規オファー」ボタンや ActionPanel の「次へ」はクリック後も
   * フォーカスを保持する。`RadioCardGroup` の Enter は「ボタン等にフォーカスが
   * あるときはその要素の決定を優先する」仕様なので、この状態で Enter を押すと
   * ホイールの決定ではなく残ったボタンが再度押され、オファーがもう 1 件
   * 作られてしまう。フォーカスを body へ戻して、キーボード操作をウィザードの
   * ホイールへ引き渡す。
   */
  useEffect(() => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;
    if (active === document.body) return;
    // ウィザード本体の中（入力欄など）に意図して当たっているフォーカスは触らない。
    if (overlayRef.current?.contains(active)) return;
    active.blur();
  }, [threadKey, entry?.currentStep]);

  // フォーム差分の自動保存（serverDmId が無いと no-op）
  useOfferDraftAutosave({ threadKey, projectId, entry });

  // ─── Detail / ロール解決のハイドレート ──────────────────────
  // ドラフトを再開した（既存の `dm:<id>` キーで開く）タイミングで以下を順番に行う:
  //   1. `getDmThreadDetail` の `offer_form` を `OfferFormState` にマージ
  //   2. ロール一覧から `assigned_roles` を逆引きして `roleId` / `roleName` を復元
  //
  // この **順序固定** が重要。並列に処理すると、`roleId` が先に決まって
  // `<DetailFormStep>` がマウントされ、プリフィル useEffect が `特記事項` 等の
  // フィールドにテンプレート値を書き込んでしまう。後から detail が届いても
  // 「フィールドが既に定義済み」と判定されてハイドレートが効かなくなる。
  const { data: detailData } = useGetDmThreadDetailQuery(
    entry?.serverDmId
      ? { project_id: projectId, dm_id: entry.serverDmId }
      : { project_id: "", dm_id: "" },
    { skip: !entry?.serverDmId },
  );

  // ロール一覧は以下 2 用途で参照する:
  //  1. ドラフト復元時の roleId 逆引き
  //  2. arc モードのロールプレビュー（SecondaryPanel）に role_name_jp / description を出す
  //
  // (2) は新規ドラフト（`serverDmId` がまだ無い状態）で role を先に選んだ場合にも
  // すぐ表示したいので、`skip` 条件で `serverDmId` を要求しない。
  // `useGetRolesQuery` は app 全体でキャッシュ共有なので、wizard 起動中だけ叩いても副作用なし。
  const { data: rolesListData } = useGetRolesQuery({ limit: 100 });

  // ── 副パネル: 選択中ユーザー詳細用のフォロー / ロール×ユーザー一覧 ─────
  // `RecipientStep` が同じ引数で叩いている `useGetFollowsQuery` /
  // `useGetRoleUsersQuery` を、ここでもキャッシュ共有目的で参照する。
  // 同一クエリ引数で RTK Query が同一キャッシュキーになるため追加リクエストは発生しない。
  const recipient = entry?.data.recipient;
  const recipientUserId =
    recipient?.mode === "follows" ? recipient.userId : undefined;
  const isRoleFirst = entry?.stepOrder === "role_first";
  const recipientRoleFilterId = isRoleFirst ? entry?.data.roleId : undefined;
  const isRecipientRoleFiltered = recipientRoleFilterId !== undefined;
  const recipientPreviewEnabled =
    entry?.currentStep === "recipient" &&
    recipient?.mode === "follows" &&
    recipientUserId !== undefined;

  const { data: previewFollowsData } = useGetFollowsQuery(
    { limit: 100 },
    {
      skip: !recipientPreviewEnabled || isRecipientRoleFiltered,
    },
  );
  const { data: previewRoleFollowingData } = useGetRoleUsersQuery(
    {
      role_id: recipientRoleFilterId as number,
      following: true,
      limit: 100,
    },
    { skip: !recipientPreviewEnabled || !isRecipientRoleFiltered },
  );
  const { data: previewRoleAcceptingData } = useGetRoleUsersQuery(
    {
      role_id: recipientRoleFilterId as number,
      accepting_offers: true,
      limit: 100,
    },
    { skip: !recipientPreviewEnabled || !isRecipientRoleFiltered },
  );

  // 選択中ユーザーの FollowUser オブジェクトを取得。
  // - 通常モード: フォロー中一覧から find
  // - ロール先モード: following / accepting_offers の和集合から find（following 優先）
  const selectedRecipientUser: FollowUser | undefined = useMemo(() => {
    if (!recipientPreviewEnabled || recipientUserId === undefined) return undefined;
    if (!isRecipientRoleFiltered) {
      return previewFollowsData?.data?.users.find(
        (u) => u.id === recipientUserId,
      );
    }
    const fromFollowing = previewRoleFollowingData?.data?.users.find(
      (u) => u.id === recipientUserId,
    );
    if (fromFollowing) return fromFollowing;
    return previewRoleAcceptingData?.data?.users.find(
      (u) => u.id === recipientUserId,
    );
  }, [
    recipientPreviewEnabled,
    recipientUserId,
    isRecipientRoleFiltered,
    previewFollowsData,
    previewRoleFollowingData,
    previewRoleAcceptingData,
  ]);

  const hydratedDetailDmIdRef = useRef<string | null>(null);
  const roleResolvedDmIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!entry?.serverDmId) return;
    const dmId = entry.serverDmId;

    // ── ステップ 1: Detail (offer_form) のハイドレート ─────
    if (hydratedDetailDmIdRef.current !== dmId) {
      if (!detailData?.data) return; // detail 到着待ち
      dispatch(
        hydrateOfferFormFromDetail({
          dmId,
          thread: detailData.data.thread,
          offerForm: detailData.data.offer_form,
        }),
      );
      hydratedDetailDmIdRef.current = dmId;
      // この dispatch で entry が更新され、effect が再実行される。
      // ロール解決はその次の実行で行う。
      return;
    }

    // ── ステップ 2: ロール ID の逆引き解決 ────────────────
    if (roleResolvedDmIdRef.current === dmId) return;

    // 既に roleId が解決済みならフラグだけ立てて終了
    if (entry.data.roleId !== undefined) {
      roleResolvedDmIdRef.current = dmId;
      return;
    }

    // 逆引き候補のラベル（送信時に保存された assigned_roles 由来）
    const label = entry.data.summary.roleLabel ?? entry.data.form.roleName;
    const roles = rolesListData?.data?.roles;
    if (!roles) return; // ロール一覧到着待ち

    if (label) {
      const matched =
        roles.find((r) => r.role_name_jp === label) ??
        roles.find((r) => r.credit_title_jp === label);
      if (matched) {
        dispatch(setRoleId({ threadKey, roleId: matched.id }));
        // `roleName` を埋めることで DetailFormStep の `isFirstFill` 判定を
        // false に倒し、ロールデフォルトでの `allowSubcontracting` 上書きを
        // 防ぐ（保存された `thread.allow_subcontracting` を尊重する）。
        dispatch(
          patchData({
            threadKey,
            patch: {
              form: {
                ...entry.data.form,
                roleName: entry.data.form.roleName ?? matched.role_name_jp,
              },
            },
          }),
        );
        roleResolvedDmIdRef.current = dmId;
        return;
      }
    }

    // 逆引きできず form ステップにいると DetailFormStep がブランクになるため、
    // role ステップへ戻して再選択を促す。
    if (entry.currentStep === "form") {
      dispatch(setStep({ threadKey, step: "role" }));
    }
    roleResolvedDmIdRef.current = dmId;
  }, [entry, detailData, rolesListData, dispatch, threadKey]);

  // `synced` 状態の表示は 2 秒で自動消去（StatusIndicator の慣例に揃える）
  useEffect(() => {
    if (entry?.syncStatus !== "synced") return;
    const timer = setTimeout(() => {
      dispatch(setSyncStatus({ threadKey, syncStatus: "idle" }));
    }, 2000);
    return () => clearTimeout(timer);
  }, [entry?.syncStatus, dispatch, threadKey]);

  // 受信者⇄ロールの整合性: 片方の選択変更により候補集合から外れた選択を自動解除する。
  // 詳細は `useOfferWizardSelectionConsistency` の JSDoc 参照。
  useOfferWizardSelectionConsistency({ threadKey, entry });

  const handleRecipientChange = useCallback(
    (next: OfferRecipientState) => {
      dispatch(patchData({ threadKey, patch: { recipient: next } }));
      if (next.mode === "email" && next.email) {
        dispatch(
          patchSummary({
            threadKey,
            patch: {
              recipientLabel: next.email,
              recipientAvatarUrl: null,
              recipientName: next.email,
            },
          }),
        );
      } else if (
        // 「メールアドレスで招待」カードを選んだだけ（アドレス未入力）、または
        // ユーザー無選択に戻った状態。どちらもサマリの宛先表示はクリアする。
        (next.mode === "email" && !next.email) ||
        (next.mode === "follows" && !next.userId)
      ) {
        dispatch(
          patchSummary({
            threadKey,
            patch: {
              recipientLabel: undefined,
              recipientAvatarUrl: undefined,
              recipientName: undefined,
            },
          }),
        );
      }
    },
    [dispatch, threadKey],
  );

  const handleRecipientUserSelected = useCallback(
    (info: RecipientStepUserSelected) => {
      dispatch(
        patchSummary({
          threadKey,
          patch: {
            recipientLabel: info.displayName,
            recipientAvatarUrl: info.avatarUrl,
            recipientName: info.displayName,
          },
        }),
      );
    },
    [dispatch, threadKey],
  );

  const handleRoleChange = useCallback(
    (roleId: number, info: { roleNameJp: string }) => {
      dispatch(setRoleId({ threadKey, roleId }));
      dispatch(
        patchSummary({
          threadKey,
          patch: { roleLabel: info.roleNameJp },
        }),
      );
      // ロール変更時はフォーム値を空にリセットして、DetailFormStep 側の
      // プリフィル useEffect が新ロールの値で全フィールドを再初期化できる
      // ようにする（前ロールの値を引きずらないため）。
      dispatch(
        patchData({
          threadKey,
          patch: { form: { allowSubcontracting: false } },
        }),
      );
    },
    [dispatch, threadKey],
  );

  /**
   * arc モードの Stage 余白クリックで「ロール未選択」状態に戻す。
   * roleLabel もクリアして、ロールサマリ表示が古い名前を引きずらないようにする。
   */
  const handleRoleDeselect = useCallback(() => {
    dispatch(setRoleId({ threadKey, roleId: undefined }));
    dispatch(
      patchSummary({
        threadKey,
        patch: { roleLabel: undefined },
      }),
    );
  }, [dispatch, threadKey]);

  /**
   * arc モードでロールカードを「再クリック」したときに、`role_name_*` ↔ `credit_title_*`
   * を切り替えるためのトグル状態。RolePicker のカード右辺縦書きと、本コンポーネントの
   * プレビューパネル（`RolePreviewBlock`）で同じ表記を共有するため、ここで保持する。
   * 選択ロールが変わったら `false`（既定 = `role_name_*`）に戻す。
   */
  const [showCreditTitle, setShowCreditTitle] = useState<boolean>(false);
  const selectedRoleIdForToggle = entry?.data.roleId;
  useEffect(() => {
    setShowCreditTitle(false);
  }, [selectedRoleIdForToggle]);

  const handleFormChange = useCallback(
    (next: OfferFormState) => {
      dispatch(patchData({ threadKey, patch: { form: next } }));
      dispatch(
        patchSummary({
          threadKey,
          patch: {
            roleLabel: next.roleName,
            scope: next.scope,
          },
        }),
      );
    },
    [dispatch, threadKey],
  );

  // プリフィル元カード（テンプレート / 過去オファー）の選択状態を保持する。
  // フォーム値そのものの反映は DetailFormStep 側が `handleFormChange` 経由で行う。
  const handlePrefillSourceChange = useCallback(
    (source: OfferPrefillSource) => {
      dispatch(setPrefillSource({ threadKey, source }));
    },
    [dispatch, threadKey],
  );

  const goToStep = useCallback(
    (step: OfferStep) => {
      dispatch(setStep({ threadKey, step }));
    },
    [dispatch, threadKey],
  );

  /**
   * 受信者ステップから次へ進む際に、まだサーバ側のドラフトレコードが無ければ作成する。
   * 成功時は **解決済みの `threadKey`**（既存または新規発行）を返す。
   *
   * 戻り値で新しい `threadKey` を返すのは、`assignServerDmId` 直後に
   * `dispatch(setStep({ threadKey: ... }))` で旧クロージャを参照すると
   * エントリ不在で no-op になり、ステップが進まない不具合（recipient → role 遷移時の
   * 「次へ反応せず」現象）を防ぐため。呼び出し側はこの返却キーで以降の dispatch を
   * 行う。
   *
   * 仕様書 §4 / BE 更新ガイド (2026-05-09) に従い、subcontract の作成は
   * 以下 2 通り（**排他**）に対応する:
   *  - `recipient.mode === "follows"`: `contractor_user_id` で既存ユーザーへ直接オファー
   *  - `recipient.mode === "email"`:   `recipient_email` でメール経由オファー
   */
  const ensureServerDraft = useCallback(async (
    opts?: { recipientOverride?: OfferRecipientState },
  ): Promise<string | null> => {
    if (!entry) return null;
    if (entry.serverDmId) return threadKey;
    // メールカード確定など、Redux 反映前の最新 recipient を直接渡せるよう override を許す
    //（dispatch 直後は entry がまだ古いクロージャ値のため）。
    const recipient = opts?.recipientOverride ?? entry.data.recipient;

    // POST ボディの組み立て（仕様書 §4 排他チェックは BE 側、FE は片方だけ送る）
    let createParams: CreateDmThreadParams;
    if (recipient.mode === "follows") {
      if (!recipient.userId) {
        setSubmitError("受信者のユーザーが選択されていません。");
        return null;
      }
      createParams = {
        project_id: projectId,
        thread_type: "subcontract",
        contractor_user_id: recipient.userId,
      };
    } else {
      // recipient.mode === "email"
      if (!recipient.email) {
        setSubmitError("有効なメールアドレスを入力してください。");
        return null;
      }
      createParams = {
        project_id: projectId,
        thread_type: "subcontract",
        recipient_email: recipient.email,
        ...(recipient.username
          ? { recipient_username: recipient.username }
          : {}),
      };
    }
    setSubmitError(null);
    try {
      dispatch(setSyncStatus({ threadKey, syncStatus: "creating" }));
      const res = await createDmThread(createParams).unwrap();
      const newDmId = res?.data?.thread?.id;
      if (!newDmId) {
        throw new Error("ドラフトの作成に失敗しました。レスポンスが不正です。");
      }
      dispatch(assignServerDmId({ fromKey: threadKey, dmId: newDmId }));
      const newKey = dmThreadKey(newDmId);

      // 既に入力済みのフィールド（ロール先順序のロール選択分など）がある場合は、
      // 続けて 1 度だけ PATCH を送って初期値を保存する。
      // `createDmThread` はリレーション作成のみで詳細フィールドを受け取らないため、
      // この初期 PATCH を送らないと autosave の初回マウント分岐に邪魔されて
      // ロール／メッセージ等が永続化されないままになる。
      const initialBody = buildOfferThreadPatchBody(entry);
      if (Object.keys(initialBody).length > 0) {
        try {
          dispatch(setSyncStatus({ threadKey: newKey, syncStatus: "saving" }));
          await updateDmThread({
            project_id: projectId,
            dm_id: newDmId,
            ...initialBody,
          }).unwrap();
        } catch (patchError) {
          console.warn(
            "[OfferWizard] initial PATCH after createDmThread failed",
            patchError,
          );
          dispatch(setSyncStatus({ threadKey: newKey, syncStatus: "error" }));
          // 初期 PATCH の失敗は致命的ではないので、ここで return せず
          // ステップ遷移自体は許可する（autosave で次のチャンスがある）。
          onThreadKeyAssigned?.(newKey);
          return newKey;
        }
      }

      dispatch(
        setSyncStatus({
          threadKey: newKey,
          syncStatus: "synced",
          lastSyncedAt: new Date().toISOString(),
        }),
      );
      onThreadKeyAssigned?.(newKey);
      return newKey;
    } catch (error) {
      console.error("[OfferWizard] createDmThread failed", error);
      setSubmitError(
        "ドラフトの保存に失敗しました。ネットワークを確認のうえ再度お試しください。",
      );
      dispatch(setSyncStatus({ threadKey, syncStatus: "error" }));
      return null;
    }
  }, [
    entry,
    dispatch,
    threadKey,
    projectId,
    createDmThread,
    updateDmThread,
    onThreadKeyAssigned,
  ]);

  /**
   * 最終確認モーダルから送信する。`updateDmThread({ status: "contractor_pending" })` で
   * BE 側に状態遷移を依頼し、成功で discardWizard + URL を `?thread=<dm_id>` に置換する。
   */
  const handleFinalizeSubmit = useCallback(async () => {
    if (!entry?.serverDmId) {
      setSubmitError(
        "ドラフトがサーバに保存されていません。最初の画面から再度オファーを作成してください。",
      );
      return;
    }
    setSubmitError(null);
    const dmId = entry.serverDmId;
    try {
      if (editMode) {
        // 編集モード: status 遷移せず、autosave の取りこぼし防止に最新差分だけ
        // flush して、送信済みオファーの内容を更新したうえでメッセンジャーへ戻る。
        const body = buildOfferThreadPatchBody(entry);
        if (Object.keys(body).length > 0) {
          await updateDmThread({
            project_id: projectId,
            dm_id: dmId,
            ...body,
          }).unwrap();
        }
        dispatch(discardWizard({ threadKey }));
        setConfirmOpen(false);
        setPdfPreview(null);
        onSubmitted?.(dmId);
        return;
      }

      await updateDmThread({
        project_id: projectId,
        dm_id: dmId,
        status: "contractor_pending",
      }).unwrap();

      // ドラフト entry は破棄（サーバ側は status: contractor_pending なので、
      // ThreadList 上で通常スレッドとして再表示される）
      dispatch(discardWizard({ threadKey }));
      setConfirmOpen(false);
      setPdfPreview(null);

      // URL を `?tab=dm&thread=<dm_id>` に置換し、DM ページの deep-link
      // ロジック側でメッセンジャー表示に切り替える。
      const next = new URLSearchParams(searchParams);
      next.set("tab", "dm");
      next.set("thread", dmId);
      setSearchParams(next, { replace: true });

      // 親に送信完了を通知し、該当スレッドを即アクティブにしてもらう。
      onSubmitted?.(dmId);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : editMode
            ? "オファーの保存に失敗しました。時間をおいて再度お試しください。"
            : "オファーの送信に失敗しました。時間をおいて再度お試しください。";
      setSubmitError(message);
    }
  }, [
    entry,
    projectId,
    updateDmThread,
    dispatch,
    threadKey,
    searchParams,
    setSearchParams,
    onSubmitted,
    editMode,
  ]);

  /**
   * 最終確認モーダル用の PDF プレビューを取得する。
   *
   * 1. autosave (debounce 1500ms) の取りこぼしを防ぐため、現在の `entry` を元に
   *    `PATCH /dm/<id>` を 1 度同期実行して BE に最新値を反映する。
   * 2. その後 `POST /api/pdf/exports`（purpose: offer_subcontract_pdf）を呼び、
   *    成功すれば Blob を `pdfPreview.ready` として保持する。
   *
   * 失敗時は `pdfPreview.error` を立て、モーダル側でサマリカード + 再試行ボタンに
   * フォールバックさせる（送信自体はブロックしない／ユーザー判断で進めるか戻る）。
   */
  const generateConfirmPdfPreview = useCallback(async () => {
    if (!entry?.serverDmId) return;
    const dmId = entry.serverDmId;
    setPdfPreview({ status: "generating" });

    try {
      // ステップ① - autosave のフラッシュ。差分が無ければ BE 側は no-op として扱う。
      const body = buildOfferThreadPatchBody(entry);
      if (Object.keys(body).length > 0) {
        try {
          await updateDmThread({
            project_id: projectId,
            dm_id: dmId,
            ...body,
          }).unwrap();
        } catch (patchError) {
          // PATCH 失敗時はそのまま PDF も諦める（古い値で生成しても齟齬を生む）。
          console.warn(
            "[OfferWizard] flush-PATCH before PDF generation failed",
            patchError,
          );
          setPdfPreview({
            status: "error",
            errorMessage:
              "最新の入力内容をサーバに保存できませんでした。ネットワーク状況をご確認のうえ、再度お試しください。",
          });
          return;
        }
      }

      // ステップ② - PDF 取得
      const { blob, filename } = await requestOfferSubcontractPdf({ dmId });
      setPdfPreview({ status: "ready", blob, filename });
    } catch (e) {
      console.error("[OfferWizard] subcontract PDF preview failed", e);
      setPdfPreview({
        status: "error",
        errorMessage: formatPdfExportError(e, {
          forbiddenMessage:
            "この契約書PDFを表示する権限がありません (403)。ご自身が発注者であることをご確認ください。",
        }),
      });
    }
  }, [entry, projectId, updateDmThread]);

  const handleNext = useCallback(async () => {
    if (!entry) return;
    const { stepOrder, currentStep } = entry;

    // 「メールアドレスで招待」カードを選んだだけ（アドレス未入力）の状態で「次へ」を
    // 押したら、ここで入力モーダルを開く。確定後は handleEmailInviteConfirmed が
    // ドラフト作成 + ステップ送りまで引き取る。送信先が①・②どちらの順序でも同じ。
    if (currentStep === "recipient") {
      const { recipient: currentRecipient } = entry.data;
      if (currentRecipient.mode === "email" && !currentRecipient.email) {
        setEmailModalOpen(true);
        return;
      }
    }

    if (currentStep === stepOneOf(stepOrder)) {
      // 受信者選択が「次へ」のタイミングならサーバ側ドラフトを作成
      let activeKey = threadKey;
      if (currentStep === "recipient") {
        const resolvedKey = await ensureServerDraft();
        if (!resolvedKey) return;
        activeKey = resolvedKey;
      }
      // `assignServerDmId` 直後は `goToStep` の closure が旧キーのままなので、
      // ensureServerDraft が返した解決済みキーで直接 setStep を投げる。
      dispatch(setStep({ threadKey: activeKey, step: stepTwoOf(stepOrder) }));
    } else if (currentStep === stepTwoOf(stepOrder)) {
      // ②がロール側 → ③詳細 へ進む手前でも、もしまだサーバ側ドラフト未作成なら作る
      let activeKey = threadKey;
      if (!entry.serverDmId) {
        const resolvedKey = await ensureServerDraft();
        if (!resolvedKey) return;
        activeKey = resolvedKey;
      }
      dispatch(setStep({ threadKey: activeKey, step: "form" }));
    } else if (currentStep === "form") {
      // 最終確認モーダルを開く（FE 側必須項目チェック）
      if (!isReadyForConfirm(entry)) {
        setSubmitError(
          "受信者・ロール・契約条件のいずれかが未入力です。入力内容をご確認ください。",
        );
        return;
      }
      if (escrowBalanceCheck.applicable && !escrowBalanceCheck.sufficient) {
        setSubmitError(
          "利用可能残高が対価合計に足りません。残高を追加するか、対価を調整してください。",
        );
        return;
      }
      setSubmitError(null);
      if (editMode) {
        // 編集モードは確認モーダルを挟まず、差分を保存してメッセンジャーに戻る。
        void handleFinalizeSubmit();
        return;
      }
      setConfirmOpen(true);
      if (entry.data.recipient.mode === "email") {
        // メールアドレス招待（未登録受託者）は、送信時点では契約ドラフトを作らない。
        // 最終確認は契約書 PDF を生成せず、オファー条件の要点（サマリ）だけ表示する
        //（PDF 生成失敗時のフォールバックと同じ表示）。契約ドラフトは受領側で
        // 契約者情報を入力した後に生成される。
        setPdfPreview(null);
      } else {
        // 登録済みユーザー宛は従来どおり、モーダルと同時に契約書 PDF を生成する。
        void generateConfirmPdfPreview();
      }
    }
  }, [
    entry,
    threadKey,
    dispatch,
    ensureServerDraft,
    generateConfirmPdfPreview,
    editMode,
    handleFinalizeSubmit,
    escrowBalanceCheck,
  ]);

  const handleBack = useCallback(() => {
    if (!entry) return;
    const { stepOrder, currentStep } = entry;
    if (currentStep === "form") {
      goToStep(stepTwoOf(stepOrder));
    } else if (currentStep === stepTwoOf(stepOrder)) {
      goToStep(stepOneOf(stepOrder));
    }
  }, [entry, goToStep]);

  /**
   * arc モードの「メールアドレスで招待」モーダルを OK したときのハンドラ。
   * 受信者を email モードで確定し、サーバドラフトを作成して **そのまま次のステップ
   * へ遷移** させる（モーダルは「次へ」から開くので、OK = そのまま次へ進む）。
   *
   * `patchData`（dispatch）直後は `entry` クロージャがまだ更新前の値なので、
   * `ensureServerDraft` には確定した recipient を override で直接渡す。
   */
  const handleEmailInviteConfirmed = useCallback(
    async (email: string, username: string) => {
      if (!entry) return;
      setEmailModalOpen(false);
      const nextRecipient: OfferRecipientState = {
        mode: "email",
        email,
        ...(username ? { username } : {}),
      };
      dispatch(patchData({ threadKey, patch: { recipient: nextRecipient } }));
      dispatch(
        patchSummary({
          threadKey,
          patch: {
            recipientLabel: email,
            recipientName: username || email,
            recipientAvatarUrl: null,
          },
        }),
      );
      setSubmitError(null);

      const resolvedKey = await ensureServerDraft({
        recipientOverride: nextRecipient,
      });
      if (!resolvedKey) return;

      // 受信者ステップが①なら②（ロール）へ、②（role_first）なら詳細フォームへ進む。
      const nextStep =
        stepOneOf(entry.stepOrder) === "recipient"
          ? stepTwoOf(entry.stepOrder)
          : "form";
      dispatch(setStep({ threadKey: resolvedKey, step: nextStep }));
    },
    [entry, dispatch, threadKey, ensureServerDraft],
  );

  const canGoNext = useMemo(() => {
    if (!entry) return false;
    switch (entry.currentStep) {
      case "recipient":
        return canLeaveRecipientStep(entry.data.recipient) && !isCreatingDraft;
      case "role":
        return !!entry.data.roleId && !isCreatingDraft;
      case "form":
        return (
          isReadyForConfirm(entry) &&
          !isFinalizing &&
          (!escrowBalanceCheck.applicable || escrowBalanceCheck.sufficient)
        );
      case "complete":
        return false;
    }
  }, [entry, isCreatingDraft, isFinalizing, escrowBalanceCheck]);

  /**
   * 送信先 / ロールカードの「決定」（ダブルクリック / Enter）で「次へ」を実行する。
   *
   * Enter は選択（`onChange`）と同じフレームで届くため、その場で判定すると
   * 「1 回目の Enter は選択だけで進まない」という食い違いが出る。次フレームまで
   * 待って、ref 経由で選択反映後の判定とハンドラを読む。ActionPanel の「次へ」が
   * 押せない状態（未選択・ドラフト作成中など）では何もしない。
   */
  const canGoNextRef = useRef(canGoNext);
  canGoNextRef.current = canGoNext;
  const handleNextRef = useRef(handleNext);
  handleNextRef.current = handleNext;

  const handleCardConfirm = useCallback(() => {
    requestAnimationFrame(() => {
      if (!canGoNextRef.current) return;
      void handleNextRef.current();
    });
  }, []);

  // 確認モーダルに渡すサマリデータ。対価合計と requires_deliverable の解決ロジックは
  // autosave 側と同じ方式に揃えて、表示と PATCH ボディで値が食い違わないようにする。
  const confirmData = useMemo<OfferSummaryCardData | null>(() => {
    if (!entry) return null;
    const form = entry.data.form;
    const summary = entry.data.summary;
    const requiresDeliverable =
      form.requiresDeliverable !== undefined
        ? form.requiresDeliverable
        : !!(form.deliverableFormat && form.deliverableFormat.trim().length > 0);
    return {
      recipientLabel: summary.recipientLabel ?? null,
      recipientAvatarUrl: summary.recipientAvatarUrl ?? null,
      assignedRoles: summary.roleLabel ?? form.roleName ?? null,
      scope: form.scope ?? null,
      deadline: form.deadline ?? null,
      contractAmount: sumCompensationLines(form.compensationLines),
      advancePaymentRate: form.advancePaymentRate ?? null,
      acceptPosition: form.acceptPosition ?? null,
      requiresDeliverable,
      allowSubcontracting: !!form.allowSubcontracting,
      initialMessage: form.message ?? null,
    };
  }, [entry]);

  if (!entry) {
    // 描画タイミング差で entry が未生成のときは何も出さない
    return null;
  }

  // 戻るは ①以外の入力ステップ（②および③詳細）でのみ有効。
  const canGoBack =
    entry.currentStep !== stepOneOf(entry.stepOrder) &&
    entry.currentStep !== "complete";

  const steps = orderedSteps(entry.stepOrder);
  const stepperCurrent = offerStepToStepNumber(
    entry.stepOrder,
    entry.currentStep,
  );
  const stepLabels = buildStepLabels(entry.stepOrder);

  const handleStepperSelect = (stepNumber: number) => {
    if (stepNumber === 4) return; // ④完了 へは送信成功後のみ
    if (stepNumber === stepperCurrent) return;

    // ②クリック時、①が未選択ならステップ順を入れ替える。
    if (stepNumber === 2 && isStepOneEmpty(entry)) {
      const nextOrder: StepOrder =
        entry.stepOrder === "recipient_first" ? "role_first" : "recipient_first";
      dispatch(setStepOrder({ threadKey, stepOrder: nextOrder }));
      goToStep(stepOneOf(nextOrder));
      return;
    }

    const target = steps[stepNumber - 1];
    if (!target || target === "complete") return;
    goToStep(target);
  };

  const isStepClickable = (stepNumber: number): boolean => {
    if (stepNumber === stepperCurrent) return false;
    if (stepNumber === 4) return false;
    if (stepNumber === 3 && !entry.data.roleId) return false;
    return true;
  };

  const nextLabel =
    entry.currentStep === "form"
      ? isFinalizing
        ? editMode
          ? "更新中..."
          : "送信中..."
        : editMode
          ? "内容を更新"
          : "最終確認へ"
      : isCreatingDraft
        ? "保存中..."
        : "次へ";

  return (
    <OverlayRoot ref={overlayRef} role="dialog" aria-label="オファー作成">
      <OverlayHeader ref={headerRef}>
        <StepFlowHeader>
          <Stepper
            stepLabels={stepLabels}
            current={stepperCurrent}
            showCheckOnComplete
            onSelectStep={handleStepperSelect}
            isStepClickable={isStepClickable}
          />
        </StepFlowHeader>
      </OverlayHeader>

      <OverlayBody
        $bottomPadding={bodyPaddingBottom}
        data-offer-overlay-scroll=""
        {...bodyScrollSync}
      >
        {entry.currentStep === "recipient" && (
          <RecipientStep
            recipient={entry.data.recipient}
            onChange={handleRecipientChange}
            onUserSelected={handleRecipientUserSelected}
            onRequestEmailInput={() => setEmailModalOpen(true)}
            onStackChange={setWheelStacked}
            onConfirmSelection={handleCardConfirm}
            wheelLayout={wheelLayout}
            roleFilterRoleId={
              entry.stepOrder === "role_first" ? entry.data.roleId : undefined
            }
            variant={USE_ARC_PICKER ? "arc" : "coverflow"}
          />
        )}
        {entry.currentStep === "role" && (
          <RoleStep
            roleId={entry.data.roleId}
            onChange={handleRoleChange}
            onDeselect={handleRoleDeselect}
            onStackChange={setWheelStacked}
            onConfirmSelection={handleCardConfirm}
            wheelLayout={wheelLayout}
            recipient={entry.data.recipient}
            variant={USE_ARC_PICKER ? "arc" : "list"}
            showCreditTitle={showCreditTitle}
            onShowCreditTitleChange={setShowCreditTitle}
          />
        )}
        {entry.currentStep === "form" && entry.data.roleId !== undefined && (
          <DetailFormStep
            roleId={entry.data.roleId}
            form={entry.data.form}
            onChange={handleFormChange}
            onEscrowBalanceChange={handleEscrowBalanceChange}
            reservedByOtherDraftsExclusive={reservedByOtherDraftsExclusive}
            // 過去オファー履歴からのプリフィルは新規作成フローのみ。
            // 編集モード（送信済みオファーの内容修正）でテンプレート/履歴への
            // 丸ごと切り替えを許すと、送信済み内容を意図せず上書きしやすいため出さない。
            recipient={editMode ? undefined : entry.data.recipient}
            excludeDmId={entry.serverDmId}
            prefillSource={entry.prefillSource}
            onPrefillSourceChange={handlePrefillSourceChange}
          />
        )}
        {entry.currentStep === "complete" && (
          <CompleteStep
            onClose={() => dispatch(discardWizard({ threadKey }))}
          />
        )}
        {submitError && entry.currentStep === "form" && (
          <ErrorText>{submitError}</ErrorText>
        )}
      </OverlayBody>

      {/*
        固定パネル群（副パネル / ActionPanel / SyncStatus）は position: fixed だが、
        SP では祖先（TwoColumnLayout のラッパ）に transform があり、CSS 仕様上
        transform 祖先を持つ fixed 子孫は viewport ではなく祖先がコンテナンブロックに
        なる。そのため BottomAppBar 連動の `bottom` 計算がビューポート下端を基準に
        効かず、「常に同じ位置に浮いて BottomAppBar に追従しない」現象が起きていた。
        DmActionPanel と同様、document.body へ Portal して viewport 基準の fixed に直す。
        left / width は overlayRect（getBoundingClientRect = viewport 座標）由来なので
        Portal 先でもそのまま使える。
      */}
      {createPortal(
        <>
          {/*
        ActionPanel の真上に出す副パネル。
         - 送信先ステップ × arc モード: 選択中ユーザー詳細（任意）+ メアド招待ブロック
         - ロールステップ × arc モード: 選択中ロールの role_name_jp / description プレビュー
        `bottom` は ActionPanel と同じ系統で計算するため、BottomAppBar の
        表示/非表示に合わせて自然に追従する。
      */}
          {(() => {
        if (!USE_ARC_PICKER) return null;
        // Shift ホールドのスタックプレビュー中は、ホームのやることホイールと同じく
        // カードの山だけの画面にする（副パネルを畳む）。
        if (wheelStacked) return null;
        let content: React.ReactNode = null;
        if (entry.currentStep === "recipient") {
          // 選択中ユーザーが居ればその詳細プレビューを出す。メールアドレス招待は
          // ホイール内の「メールアドレスで招待」カードが入口なので、ここには出さない。
          content = selectedRecipientUser ? (
            <UserPreviewContent user={selectedRecipientUser} />
          ) : null;
        } else if (
          entry.currentStep === "role" &&
          entry.data.roleId !== undefined
        ) {
          const selectedRole = rolesListData?.data?.roles?.find(
            (r) => r.id === entry.data.roleId,
          );
          if (selectedRole) {
            const previewIcon = roleStatusIcon(
              selectedRole.accept_position,
              selectedRole.allow_subcontracting,
            );
            const previewIconLabel = roleStatusLabel(
              selectedRole.accept_position,
              selectedRole.allow_subcontracting,
            );
            // 「同じカードを再クリック」での切替に追従して、表示する日本語名 / 英語名を
            // role_name_* と credit_title_* の間でトグルする（カード右辺縦書きと同じ
            // 優先度ロジック。選んだ側が空ならもう一方にフォールバック）。
            const previewNameJp = pickRoleDisplayName(
              selectedRole.role_name_jp,
              selectedRole.credit_title_jp,
              showCreditTitle,
            );
            const previewNameEn = pickRoleDisplayName(
              selectedRole.role_name_en,
              selectedRole.credit_title_en,
              showCreditTitle,
            );
            content = (
              <RolePreviewBlock>
                <RolePreviewIcon
                  role={previewIcon ? "img" : undefined}
                  aria-label={previewIcon ? previewIconLabel ?? undefined : undefined}
                  aria-hidden={previewIcon ? undefined : true}
                  $compact={previewIcon === "hub"}
                >
                  {previewIcon ?? ""}
                </RolePreviewIcon>
                <RolePreviewBody>
                  <RolePreviewTitleRow>
                    {previewNameJp ? (
                      <RolePreviewTitle>{previewNameJp}</RolePreviewTitle>
                    ) : null}
                    {previewNameEn ? (
                      <RolePreviewTitleEn>{previewNameEn}</RolePreviewTitleEn>
                    ) : null}
                  </RolePreviewTitleRow>
                  {selectedRole.description && (
                    <RolePreviewDescription>
                      {selectedRole.description}
                    </RolePreviewDescription>
                  )}
                </RolePreviewBody>
              </RolePreviewBlock>
            );
          }
        }
        if (!content) return null;
        return (
          <SecondaryPanel
            ref={secondaryPanelRef}
            $bottom={
              actionPanelBottom + footerHeight + ACTION_PANEL_BOTTOM_BUFFER
            }
            $left={overlayRect.left}
            $width={overlayRect.width}
          >
            {content}
          </SecondaryPanel>
        );
      })()}

      {entry.currentStep !== "complete" && (
        <WizardActionPanel
          ref={footerRef}
          $bottom={actionPanelBottom}
          $left={overlayRect.left}
          $width={overlayRect.width}
        >
          <WizardActionPanelInner>
            <Button
              size="small"
              variant="filled"
              theme="tertiary"
              leftIcon="chevron_left"
              disabled={!canGoBack}
              onClick={handleBack}
            >
              戻る
            </Button>
            <Button
              size="small"
              rightIcon="chevron_right"
              disabled={!canGoNext}
              onClick={handleNext}
            >
              {nextLabel}
            </Button>
          </WizardActionPanelInner>
        </WizardActionPanel>
      )}

      {/*
        ドラフト保存ステータス（右モーダルの右下にフロート表示）。
        WizardActionPanel と、その上に出る SecondaryPanel（arc モード時のみ高さあり）の
        合計分上に重ならないようオフセットする。`actionPanelBottom` には BottomAppBar の
        表示/非表示が反映されるので、結果としてここも自動的に追従する。
      */}
      <SyncStatusFloater
        $bottom={
          actionPanelBottom +
          footerHeight +
          secondaryPanelHeight +
          SYNC_STATUS_GAP_ABOVE_ACTION_PANEL
        }
      >
        <StatusIndicator
          inline
          status={toGlobalStatusType(entry.syncStatus)}
        />
      </SyncStatusFloater>
        </>,
        document.body,
      )}

      {/*
        「メールアドレスで招待」カードを選んで「次へ」を押したとき（または選択済み
        カードを再クリックしてアドレスを編集するとき）の入力モーダル。
        OK でドラフト作成 + 次ステップ送りまで handleEmailInviteConfirmed が行う。
      */}
      {emailModalOpen && (
        <EmailInviteModal
          initialEmail={entry.data.recipient.email ?? ""}
          initialUsername={entry.data.recipient.username ?? ""}
          onClose={() => setEmailModalOpen(false)}
          onConfirm={(email, username) => {
            void handleEmailInviteConfirmed(email, username);
          }}
        />
      )}

      {confirmOpen && confirmData && (
        <OfferConfirmModal
          data={confirmData}
          onClose={() => {
            if (isFinalizing) return;
            setConfirmOpen(false);
            setPdfPreview(null);
          }}
          onSubmit={() => {
            void handleFinalizeSubmit();
          }}
          isSubmitting={isFinalizing}
          errorMessage={submitError}
          pdfPreview={pdfPreview ?? undefined}
          onRetryPdf={() => {
            void generateConfirmPdfPreview();
          }}
        />
      )}
    </OverlayRoot>
  );
};

export default OfferWizardOverlay;
